import { signOut } from "firebase/auth";
import { authFirebase } from "../../config";
import {
  ActionIcon,
  Box,
  Button,
  ButtonGroup,
  Card,
  Center,
  Container,
  Flex,
  Group,
  Image,
  Loader,
  LoadingOverlay,
  Modal,
  Paper,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { IconEye, IconLogout, IconPencil, IconPlus, IconSearch, IconTrash } from "@tabler/icons-react";
import { useUserStore } from "../../store/user.store";
import { notifications } from "@mantine/notifications";
import MainDrawer from "./components/MainDrawer";
import { useDrawerStore } from "../../store/dashboard.store";
import AddEvent from "./components/AddEvent";
import { useEventStore } from "../../store/events.store";
import { useEffect, useState } from "react";
import ViewEvent from "./components/ViewEvent";
import EditEvent from "./components/EditEvent";
import dayjs from "dayjs";
import { useDisclosure } from "@mantine/hooks";
import { useScreenSize } from "../../utilities/use-screensize";
import CardEvent from "./components/CardEvent";
import ViewPatient from "./components/ViewPatient";

const logout = () => {
  signOut(authFirebase);
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const clearUserInfo = useUserStore((state) => state.clearUserInfo);
  const addPage = useDrawerStore((state) => state.addPage);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const {
    getEvents,
    events,
    deleteEvent,
    history,
    getHistoryOfEvents,
    loadMoreHistoryOfEvents,
    hasMoreHistory,
  } = useEventStore((state) => state);
  const [opened, { open, close }] = useDisclosure(false);
  const [deleteUid, setDeleteUid] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { isMobile } = useScreenSize();

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      await getEvents();
      await getHistoryOfEvents();
      setLoading(false);
    };
    fetchEvents();
  }, [getEvents, getHistoryOfEvents]);

  const handleLogout = () => {
    logout();
    clearUserInfo();
    navigate("/login");
    notifications.show({
      title: "Success",
      message: "You have successfully logged out",
      color: "clinic",
    });
  };

  const openDeleteConfirm = (uid: string) => {
    setDeleteUid(uid);
    open();
  };

  const handleLoadMoreHistory = async () => {
    setHistoryLoading(true);
    await loadMoreHistoryOfEvents();
    setHistoryLoading(false);
  };

  const renderEventActions = (
    event: (typeof events)[number],
  ) => (
    <ButtonGroup>
      <Button
        leftSection={<IconEye size={16} />}
        size="xs"
        color="clinic"
        variant="light"
        onClick={() => {
          addPage(<ViewEvent event={event} />, `View Page (Event Id: ${event.uid})`);
        }}
      >
        View
      </Button>
      <Button
        leftSection={<IconPencil size={16} />}
        size="xs"
        color="clinic"
        variant="light"
        onClick={() => addPage(<EditEvent event={event} />, `Edit Page (Event Id: ${event.uid})`)}
      >
        Edit
      </Button>
      {(event.patientTotal ?? 0) === 0 && (
        <Button
          leftSection={<IconTrash size={16} />}
          size="xs"
          color="accent"
          variant="light"
          onClick={() => openDeleteConfirm(event.uid!)}
        >
          Delete
        </Button>
      )}
    </ButtonGroup>
  );

  return (
    <Container
      size="xl"
      py="lg"
    >
      <Stack gap="lg">
        <Paper withBorder radius="xl" p="lg" shadow="xs">
          <Flex align="center" justify="space-between" gap="md">
            <Group gap="md" wrap="nowrap">
              <Image
                src="assets/st_mina.png"
                alt="St. Mina icon"
                h={52}
                w={52}
                fit="contain"
              />
              <div>
                <Title order={2}>St. Mina Clinic Dashboard</Title>
                <Text c="var(--my-dark)" size="sm" mt={4}>
                  Manage upcoming appointments, history, and patient registration.
                </Text>
              </div>
            </Group>
            {!isMobile && (
              <Group>
                <Button
                  leftSection={<IconSearch size={18} />}
                  color="clinic"
                  variant="light"
                  onClick={() => addPage(<ViewPatient />, "Patient Sign Up History")}
                >
                  Search Patient
                </Button>
                <Button
                  leftSection={<IconLogout size={18} />}
                  color="accent"
                  variant="light"
                  onClick={handleLogout}
                >
                  Log Out
                </Button>
              </Group>
            )}
            {isMobile && (
              <Group gap="xs">
                <ActionIcon
                  variant="light"
                  color="clinic"
                  size="lg"
                  onClick={() => addPage(<ViewPatient />, "Patient Sign Up History")}
                >
                  <IconSearch size={20} />
                </ActionIcon>
                <ActionIcon variant="light" color="accent" size="lg" onClick={handleLogout}>
                  <IconLogout size={20} />
                </ActionIcon>
              </Group>
            )}
          </Flex>
        </Paper>

        <Card withBorder radius="xl" padding="md" className="scroll-h" shadow="xs">
          <Flex align="center" justify="space-between" gap="md">
            <div>
              <Title order={3}>Upcoming Events</Title>
              <Text c="var(--my-dark)" size="sm">
                Review and manage active appointment slots.
              </Text>
            </div>
            <Button
              color="clinic"
              leftSection={<IconPlus size={18} />}
              radius="xl"
              onClick={() => addPage(<AddEvent />, "Add Event")}
            >
              Add Event
            </Button>
          </Flex>
          <Card.Section p="md">
            {loading && (
              <Center>
                <Loader size={40} color="clinic" />
              </Center>
            )}
            {!loading && events.length > 0 && !isMobile && (
              <Table layout="auto">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Registered</Table.Th>
                    <Table.Th>Limit</Table.Th>
                    <Table.Th>Action</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {events.map((event) => (
                    <Table.Tr key={event.uid}>
                      <Table.Td>{event.type}</Table.Td>
                      <Table.Td>{dayjs(event.date).format("MMM DD YYYY h:mm A")}</Table.Td>
                      <Table.Td>{event.patientTotal}</Table.Td>
                      <Table.Td>{event.limit}</Table.Td>
                      <Table.Td align="center">{renderEventActions(event)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            {!loading &&
              events.length > 0 &&
              isMobile &&
              events.map((event) => (
                <CardEvent
                  key={event.uid}
                  event={event}
                  setDeleteUid={() => openDeleteConfirm(event.uid!)}
                />
              ))}
            {!loading && events.length === 0 && (
              <Center>
                <Text fw={600}>No events found</Text>
              </Center>
            )}
          </Card.Section>
        </Card>

        <Card withBorder radius="xl" shadow="xs" padding="md">
          <Flex align="center" justify="space-between">
            <div>
              <Title order={3}>Event History</Title>
              <Text c="var(--my-dark)" size="sm">
                Past appointments remain available for quick reference.
              </Text>
            </div>
          </Flex>
          <Card.Section p="md">
            {loading && (
              <Center>
                <Loader size={40} color="clinic" />
              </Center>
            )}
            {!loading && history.length > 0 && !isMobile && (
              <Table layout="auto">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Registered</Table.Th>
                    <Table.Th>Limit</Table.Th>
                    <Table.Th>Action</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {history.map((event) => (
                    <Table.Tr key={event.uid}>
                      <Table.Td>{event.type}</Table.Td>
                      <Table.Td>{dayjs(event.date).format("MMM DD YYYY h:mm A")}</Table.Td>
                      <Table.Td>{event.patientTotal}</Table.Td>
                      <Table.Td>{event.limit}</Table.Td>
                      <Table.Td align="center">{renderEventActions(event)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            {!loading &&
              history.length > 0 &&
              isMobile &&
              history.map((event) => (
                <CardEvent
                  key={event.uid}
                  event={event}
                  setDeleteUid={() => openDeleteConfirm(event.uid!)}
                />
              ))}
            {!loading && history.length === 0 && (
              <Center>
                <Text fw={600}>No events found</Text>
              </Center>
            )}
          </Card.Section>
          {!loading && history.length > 0 && hasMoreHistory && (
            <Center pt="md">
              <Button
                variant="light"
                color="clinic"
                radius="xl"
                loading={historyLoading}
                onClick={handleLoadMoreHistory}
              >
                Load More
              </Button>
            </Center>
          )}
        </Card>

        <MainDrawer />

        <Modal opened={opened} onClose={close} title="Delete event?" centered radius="lg">
          <Box pos="relative">
            <LoadingOverlay visible={deleteLoading} />
            <Text size="sm" c="var(--my-dark)">
              This action cannot be undone. Are you sure you want to delete this event?
            </Text>
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={close}>
                Cancel
              </Button>
              <Button
                color="accent"
                onClick={async () => {
                  setDeleteLoading(true);
                  await deleteEvent(deleteUid!);
                  setDeleteLoading(false);
                  close();
                }}
              >
                Delete
              </Button>
            </Group>
          </Box>
        </Modal>
      </Stack>
    </Container>
  );
};

export default DashboardPage;
